import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  KoppelingRij,
  NieuwePersoon,
  PersonenOverzicht,
  Rechten,
  Seniority,
  UitnodigingRow,
} from '@vip/shared';
import { api } from '../../lib/apiClient';

/**
 * Eén uitvraag voor het hele scherm, en mutaties die niets teruggeven.
 *
 * De mutaties leverden hiervóór elk het volledige overzicht terug. Dat is één
 * vorm per route om te onderhouden, en bij een fout ergens halverwege krijg je
 * een half bijgewerkte lijst. Nu is er één lijst en één manier om hem te
 * verversen.
 */
const sleutel = ['personen'] as const;
const uitnodigingenSleutel = ['uitnodigingen'] as const;

export function usePersonen() {
  return useQuery({
    queryKey: sleutel,
    queryFn: () => api.get<PersonenOverzicht>('/personen/overzicht'),
  });
}

export function useUitnodigingen() {
  return useQuery({
    queryKey: uitnodigingenSleutel,
    queryFn: () => api.get<{ uitnodigingen: UitnodigingRow[] }>('/accounts/uitnodigingen'),
  });
}

/** Elke mutatie ververst allebei de lijsten: uitnodigen verandert de toegang van
 *  een persoon, en een persoon verwijderen haalt zijn uitnodiging weg. */
function useMutatie<T, R = unknown>(fn: (invoer: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: sleutel }),
        qc.invalidateQueries({ queryKey: uitnodigingenSleutel }),
      ]);
    },
  });
}

export const useVoegPersoonToe = () =>
  useMutatie((invoer: NieuwePersoon) => api.post('/personen', invoer));

export const useWijzigRechten = () =>
  useMutatie(({ id, ...rest }: { id: string; rechten: Rechten; bevestigd?: boolean }) =>
    api.patch(`/personen/${id}/rechten`, rest),
  );

export const useWijzigPersoon = () =>
  useMutatie(
    ({
      id,
      ...rest
    }: {
      id: string;
      naam?: string;
      niveau?: Seniority | null;
      opdrachtIds?: string[];
      /** Leeg is een adres weghalen; dat mag alleen een Admin. */
      email?: string | null;
    }) => api.patch(`/personen/${id}`, rest),
  );

export const useZetActief = () =>
  useMutatie(({ id, actief }: { id: string; actief: boolean }) =>
    api.patch(`/personen/${id}/actief`, { actief }),
  );

export const useArchiveer = () =>
  useMutatie((id: string) => api.post(`/personen/${id}/archiveren`));

export const useZetTerug = () =>
  useMutatie((id: string) => api.post(`/personen/${id}/terugzetten`));

export const useVerwijderDefinitief = () => useMutatie((id: string) => api.del(`/personen/${id}`));

/** Het adres staat op de persoon, dus de uitnodiging heeft alleen zijn id nodig. */
export const useNodigUit = () => useMutatie((id: string) => api.post(`/personen/${id}/uitnodigen`));

export const useKoppel = () =>
  useMutatie((invoer: { buddyId: string; medewerkerId: string }) =>
    api.post('/personen/koppelingen', invoer),
  );

export const useBeeindigKoppeling = () =>
  useMutatie((id: string) => api.post(`/personen/koppelingen/${id}/beeindigen`));

export const useMaakOpdracht = () =>
  useMutatie((invoer: { opdrachtgever: string; opdracht: string }) =>
    api.post<{ opdrachtId: string }>('/personen/opdrachten', invoer),
  );

export const useVerwijderOpdracht = () =>
  useMutatie((id: string) => api.del(`/personen/opdrachten/${id}`));

export const useStuurUitnodigingOpnieuw = () =>
  useMutatie((id: string) => api.post(`/accounts/uitnodigingen/${id}/opnieuw`));

export const useTrekUitnodigingIn = () =>
  useMutatie((id: string) => api.del(`/accounts/uitnodigingen/${id}`));

/** De historie wordt per persoon opgehaald, niet met het overzicht mee: het is
 *  een lijst die je opent, en meesturen zou elke lading vergroten voor iets wat
 *  meestal niet wordt bekeken. */
export function useHistorie(persoonId: string | null) {
  return useQuery({
    queryKey: ['persoon-historie', persoonId],
    queryFn: () => api.get<{ koppelingen: KoppelingRij[] }>(`/personen/${persoonId}/historie`),
    enabled: Boolean(persoonId),
  });
}

/** De drempel deelt de route met de bewaartermijnen; het scherm stuurt alleen
 *  zijn eigen sleutel mee. */
export function useZetDrempel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weken: number) => api.post('/admin/instellingen', { gesprekDrempelWeken: weken }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sleutel }),
  });
}
